
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Shield, Mail } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-racing-dark text-white">
      <Helmet>
        <title>Privacy Policy | Front Runner: Racehorse Manager</title>
        <meta name="description" content="Front Runner: Racehorse Manager's privacy policy. Learn how we collect, use, and protect your information when you play our horse racing management game." />
        <meta name="keywords" content="front runner privacy policy, horse racing game privacy, data protection, user information" />
        
        {/* Primary Meta Tags */}
        <meta itemProp="name" content="Privacy Policy | Front Runner: Racehorse Manager" />
        <meta itemProp="description" content="Learn how we collect, use, and protect your information when you play Front Runner: Racehorse Manager." />
        <meta itemProp="image" content="https://c0b7ec99-c595-43b9-be02-b9d207bd20c5.lovableproject.com/lovable-uploads/FRRM-trailerImage.webp" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://frontrunnergame.com/privacy" />
        <meta property="og:title" content="Privacy Policy | Front Runner: Racehorse Manager" />
        <meta property="og:description" content="Learn how we collect, use, and protect your information when you play Front Runner: Racehorse Manager." />
        <meta property="og:image" content="https://c0b7ec99-c595-43b9-be02-b9d207bd20c5.lovableproject.com/lovable-uploads/FRRM-trailerImage.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://frontrunnergame.com/privacy" />
        <meta name="twitter:title" content="Privacy Policy | Front Runner: Racehorse Manager" />
        <meta name="twitter:description" content="Learn how we collect, use, and protect your information when you play Front Runner: Racehorse Manager." />
        <meta name="twitter:image" content="https://c0b7ec99-c595-43b9-be02-b9d207bd20c5.lovableproject.com/lovable-uploads/FRRM-trailerImage.webp" />
        
        {/* Canonical link */}
        <link rel="canonical" href="https://frontrunnergame.com/privacy" />
      </Helmet>
      <Navigation />
      <main className="container mx-auto px-4 py-24 md:py-32">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center mb-8">
            <div className="h-12 w-12 rounded-full bg-[#f94933]/10 flex items-center justify-center">
              <Shield className="h-6 w-6 text-[#f94933]" />
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-8">Privacy Policy</h1>
          
          <div className="space-y-8 text-white/80">
            <section className="space-y-4">
              <p>
              Front Runner: Racehorse Manager is brought to you by The Winners Circle in partnership with Roll1ng Thund3rz Ltd, who (“TWC”, “RTz”, "we," "us," or "our") respect your privacy and are committed to protecting it through our compliance with this privacy notice ("Privacy Notice"). This Privacy Notice applies to the website frontrunnergame.io ("Website") and the application Front Runner : Racehorse Manager(“Game”). Please read this Privacy Notice carefully before using the Website.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-white">Information We Collect</h2>
              <p>We collect several types of information from and about users of our Website, including:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><span className="font-semibold text-white">Information you provide to us:</span> We may collect information that you provide to us when you fill out forms on our Website or correspond with us through our Website or otherwise.</li>
                <li><span className="font-semibold text-white">Automatic information:</span> We may automatically collect certain information about your equipment, browsing actions and patterns when you access or use our Website, such as your IP address, browser type, referring/exit pages, and operating system.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-white">Cookies, Google and Unity Analytics</h2>
              <p>
                Our Website may use "cookies" and similar technologies to improve user experience, monitor Website performance, and provide customized content. Cookies are small text files that are placed on your device by a web server when you access our Website. We use both session cookies (which expire once you close your web browser) and persistent cookies (which stay on your device until they expire or you delete them).
              </p>
              <p>
                Our Website and Game may also use Google Analytics, a web analytics service provided by Google, Inc. ("Google"), as well as Unity Analytics, another web service provided by Unity Technologies Ltd ("Unity"). Both Google Analytics and Unity Analytics use cookies to analyze how users interact with our Website and Game. The information generated by the cookie about your use of our Website and Game (including your IP address) may be transmitted to and stored by Google and Unity on servers in the United States. Both Google and Unity will use this information for the purpose of evaluating your use of our Website and Game, compiling reports on activity for operators and providing other services relating to website activity and internet usage. Google and Unity may also transfer this information to third parties where required to do so by law, or where such third parties process the information on Google's or Unity's behalf. You may refuse the use of cookies by selecting the appropriate settings on your browser, but please note that if you do this, you may not be able to use the full functionality of our Website or Game.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-white">How We Use Your Information</h2>
              <p>We may use information that we collect about you or that you provide to us:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>To provide you with information, products or services that you request from us.</li>
                <li>To carry out our obligations and enforce our rights arising from any contracts entered into between you and us, including for billing and collection.</li>
                <li>To notify you about changes to our Website, Game or any products or services we offer or provide through it.</li>
                <li>To improve our Website, Game and to deliver a better and more personalized service.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-white">Disclosure of Your Information</h2>
              <p>We may disclose personal information that we collect or you provide as described in this Privacy Notice:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>To our subsidiaries and affiliates.</li>
                <li>To contractors, service providers and other third parties we use to support our business and who are bound by contractual obligations to keep personal information confidential and use it only for the purposes for which we disclose it to them.</li>
                <li>To a buyer or other successor in the event of a merger, divestiture, restructuring, reorganisation, dissolution or other sale or transfer of some or all of our assets, whether as a going concern or as part of bankruptcy, liquidation or similar proceeding, in which personal information held by us about our Website users is among the assets transferred.</li>
                <li>To fulfill the purpose for which you provide it.</li>
                <li>For any other purpose disclosed by us when you provide the information.</li>
              </ul>
              <p>We may also disclose your personal information to comply with any court order, law or legal process, including to respond to any government or regulatory request.</p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-white">Your Choices About How We Use and Disclose Your Information</h2>
              <p>
                We strive to provide you with choices regarding the personal information you provide to us. You can set your browser to refuse all or some browser cookies or to alert you when cookies are being sent. If you disable or refuse cookies, please note that some parts of this site may then be inaccessible or not function properly.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-white">Data Security</h2>
              <p>
                We have implemented measures designed to secure your personal information from accidental loss and from unauthorized access, use, alteration and disclosure. However, the transmission of information via the internet is not completely secure. Although we do our best to protect your personal information, we cannot guarantee the security of your personal information transmitted to our Website.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-white">Changes to Our Privacy Notice</h2>
              <p>
                We may revise this Privacy Notice at any time by updating this page. Please check this page regularly to stay informed about our information practices.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-bold text-white">Contact Information</h2>
              <p>
                If you have any questions or comments about this Privacy Notice, our privacy practices, or if you would like to exercise your rights under applicable privacy laws, please contact us at:
              </p>
              <a 
                href="mailto:contact@rtz.gg" 
                className="inline-flex items-center text-racing-accent hover:text-racing-accent/80 transition-colors"
              >
                <Mail className="h-4 w-4 mr-2" />
                contact@rtz.gg
              </a>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
